import type { TFunction } from 'i18next';
import { isAxiosError } from 'axios';
import { eAlertType } from '../providers/ToastProvider/Toast.enum';
import { tAlert } from '../providers/ToastProvider/Toast.types';
import { errorBusinessDirectory, lookupError } from './ErrorDirectory';

/**
 * Wire codes whatsapp-business-service answers with when the prepaid wallet
 * blocks a send (RET-1959): insufficient balance, wallet unavailable, message
 * could not be priced.
 */
const WALLET_SEND_ERROR_CODES: readonly string[] = [
  'wba-10101',
  'wba-10102',
  'wba-10103',
];

/** The only code whose copy depends on the role (RET-2103 role delta). */
const INSUFFICIENT_BALANCE_CODE = 'wba-10101';

function readServerCode(error: unknown): string | undefined {
  const axiosErr = isAxiosError(error) ? error : undefined;
  return axiosErr?.response?.data?.code ?? axiosErr?.response?.data?.error;
}

/**
 * The localized message for a directory entry. This is the ONE place that
 * decides by role: `wba-10101` (insufficient wallet balance) tells a user who
 * cannot top up (DS-03 §3: only Owner/Admin can) to ask an admin instead of
 * telling them to "recharge". Every other code is role-independent.
 */
function messageForEntry(
  entry: { code: string | number; message: string },
  t: TFunction,
  canTopUpBalance: boolean,
): string {
  if (entry.code === INSUFFICIENT_BALANCE_CODE && !canTopUpBalance) {
    return t('wapi.walletInsufficientBalanceAskAdmin', { ns: 'errors/errors' });
  }
  return t(entry.message, { ns: 'errors/errors' });
}

/**
 * The toast for a failed send, shared by the template and interactive
 * composers so both read the same.
 *
 * Looks up the upstream wire code (whatsapp-business-service's
 * `response.data.code`, or its `error` alias) in `errorBusinessDirectory` and
 * translates the entry out of the `errors/errors` namespace (RET-2103). The
 * title comes from the matching `lookupError` entry (RET-2376). With no entry
 * it NEVER concatenates the backend's raw `message`: scope 'any' falls back to
 * the generic template send-failure copy (no title); scope 'wallet' answers
 * undefined so the caller keeps its own handling.
 *
 * `scope: 'wallet'` only recognizes the wallet codes (wba-10101/10102/10103,
 * RET-1959). The calling hook must declare `errors/errors` (and `chat/chat`
 * for scope 'any') in its useTranslation. `canTopUpBalance` is the role flag
 * (Owner/Admin), passed through as-is; the decision lives in this module.
 */
export function resolveSendErrorAlert(
  error: unknown,
  t: TFunction,
  canTopUpBalance: boolean,
  scope: 'any',
): tAlert;
export function resolveSendErrorAlert(
  error: unknown,
  t: TFunction,
  canTopUpBalance: boolean,
  scope: 'wallet',
): tAlert | undefined;
export function resolveSendErrorAlert(
  error: unknown,
  t: TFunction,
  canTopUpBalance: boolean,
  scope: 'any' | 'wallet',
): tAlert | undefined {
  const serverCode = readServerCode(error);
  const inScope =
    serverCode &&
    (scope === 'any' || WALLET_SEND_ERROR_CODES.includes(serverCode));
  const entry = inScope
    ? errorBusinessDirectory.find((e) => e.code === serverCode)
    : undefined;

  if (entry) {
    const titleKey = lookupError(entry.code)?.titleKey;
    return {
      type: eAlertType.ERROR,
      ...(titleKey ? { title: t(titleKey, { ns: 'errors/errors' }) } : {}),
      message: messageForEntry(entry, t, canTopUpBalance),
    };
  }

  if (scope === 'wallet') return undefined;
  return {
    type: eAlertType.ERROR,
    message: `${t('wabTemplatesVariablesAction.error.text')} ${t('wabTemplatesVariablesAction.error.unknown')}`,
  };
}
